/**
 * AzurePickerTree — the subscription → kind → resource tree as a WAI-ARIA tree
 * with roving tabindex (Option C, C4a): every row is a focusable `treeitem`
 * that CONTAINS its `role="group"` of children (`aria-level`, `aria-expanded`
 * on groups; single mode `aria-selected` on resources, multi mode `aria-checked`
 * true / false / mixed on every item; `aria-disabled` on already-saved ones);
 * Up / Down / Home / End move, Right and
 * Left expand / collapse or step into / out of a group, Space toggles, Enter
 * confirms a single pick (toggles in multi mode). The key model is pure
 * (`azurePickerKeyboard.ts`); this file renders and focuses.
 *
 * Single mode: the highlighted resource follows focus (`onFocus`) and a
 * double-click or Enter confirms. Multi mode: cascading tri-state checkboxes at
 * subscription and kind level plus per-row checkboxes. Selection / focus state
 * is owned by the parent.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { AzureDiscoveredResource } from '../../../types/azureDiscovery';
import type { AzureTreeGroup } from './azurePickerFilter';
import { flattenVisibleRows, kindKey, subscriptionKey, treeKeyAction, type TreeRow } from './azurePickerKeyboard';
import { GroupRow, ResourceRow, type CheckedState } from './AzurePickerTreeRows';

interface AzurePickerTreeProps {
  groups: AzureTreeGroup[];
  multiSelect: boolean;
  /** Entry mode (C3.1): Enter / double-click on a resource row confirm it (Space still toggles its checkbox). */
  confirmOnRow?: boolean;
  selectedIds: Set<string>;
  focusedId?: string;
  disabledIds: Set<string>;
  forceExpand: boolean;
  /** Rows owned by another identity (revalidation): shown, never selectable. */
  inert?: boolean;
  onToggleResource: (id: string, checked: boolean) => void;
  onToggleMany: (ids: string[], checked: boolean) => void;
  onFocus: (resource: AzureDiscoveredResource) => void;
  onConfirmResource: (resource: AzureDiscoveredResource) => void;
}

function triState(enabledIds: string[], selected: Set<string>): CheckedState {
  if (enabledIds.length === 0) return false;
  const count = enabledIds.filter((id) => selected.has(id)).length;
  if (count === 0) return false;
  return count === enabledIds.length ? true : 'indeterminate';
}

export function AzurePickerTree(props: AzurePickerTreeProps) {
  const { groups, multiSelect, confirmOnRow = false, selectedIds, focusedId, disabledIds, forceExpand, inert = false } = props;
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined);
  const treeRef = useRef<HTMLDivElement>(null);
  const pendingFocus = useRef<string | undefined>(undefined);

  const isOpen = (key: string) => forceExpand || !collapsed.has(key);
  const setOpen = (key: string, open: boolean) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (open) next.delete(key);
      else next.add(key);
      return next;
    });
  const rows = useMemo(() => flattenVisibleRows(groups, isOpen, disabledIds), [groups, collapsed, forceExpand, disabledIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // One tab stop: the active row (the highlighted resource, else the first row).
  const tabStopKey = rows.some((row) => row.key === activeKey) ? activeKey : rows.some((row) => row.key === focusedId) ? focusedId : rows[0]?.key;

  useEffect(() => {
    const key = pendingFocus.current;
    if (!key) return;
    pendingFocus.current = undefined;
    focusRow(key);
  });

  const enabledOf = (list: AzureDiscoveredResource[]) => list.map((r) => r.id).filter((id) => !disabledIds.has(id));
  const groupEnabled = new Map<string, string[]>();
  for (const group of groups) {
    const all = group.kinds.flatMap((k) => k.resources);
    groupEnabled.set(subscriptionKey(group.subscriptionId), enabledOf(all));
    for (const kind of group.kinds) groupEnabled.set(kindKey(group.subscriptionId, kind.kind), enabledOf(kind.resources));
  }

  const focusRow = (key: string): boolean => {
    const element = treeRef.current?.querySelector<HTMLElement>(`[data-row-key="${cssEscape(key)}"]`);
    element?.focus();
    return Boolean(element);
  };

  const moveTo = (key: string) => {
    setActiveKey(key);
    // A visible row is focused at once; a row that appears with the next render (after an expand) is focused then.
    if (!focusRow(key)) pendingFocus.current = key;
    const row = rows.find((r) => r.key === key);
    if (row?.resource && !row.disabled && !inert) props.onFocus(row.resource);
  };

  const toggleRow = (row: TreeRow) => {
    if (inert) return;
    if (row.resource) {
      if (row.disabled) return;
      if (multiSelect) props.onToggleResource(row.resource.id, !selectedIds.has(row.resource.id));
      else props.onFocus(row.resource);
      return;
    }
    if (multiSelect) {
      const ids = groupEnabled.get(row.key) ?? [];
      props.onToggleMany(ids, triState(ids, selectedIds) !== true);
    } else {
      setOpen(row.key, !isOpen(row.key));
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    // The row the key was pressed on is the active row (focus may not have flushed into state yet).
    const targetKey = (event.target as HTMLElement).closest?.('[data-row-key]')?.getAttribute('data-row-key') ?? undefined;
    const action = treeKeyAction(rows, targetKey ?? activeKey ?? tabStopKey, event.key);
    if (action.type === 'none') return;
    event.preventDefault();
    event.stopPropagation();
    switch (action.type) {
      case 'move':
        moveTo(action.key);
        break;
      case 'expand':
        setOpen(action.key, true);
        break;
      case 'collapse':
        setOpen(action.key, false);
        break;
      case 'toggle': {
        const row = rows.find((r) => r.key === action.key);
        if (row) toggleRow(row);
        break;
      }
      case 'confirm': {
        const row = rows.find((r) => r.key === action.key);
        if (!row) break;
        if (!row.resource) setOpen(row.key, !isOpen(row.key));
        else if (inert || row.disabled) break;
        else if (multiSelect && !confirmOnRow) props.onToggleResource(row.resource.id, !selectedIds.has(row.resource.id));
        else props.onConfirmResource(row.resource);
        break;
      }
    }
  };

  return (
    <div
      ref={treeRef}
      className="flex flex-col"
      role="tree"
      aria-label="Azure resources"
      aria-multiselectable={multiSelect || undefined}
      data-testid="azure-picker-tree"
      onKeyDown={onKeyDown}
    >
      {groups.map((group) => {
        const subKey = subscriptionKey(group.subscriptionId);
        const allSubResources = group.kinds.flatMap((k) => k.resources);
        const subEnabled = groupEnabled.get(subKey) ?? [];
        return (
          <GroupRow
            key={subKey}
            rowKey={subKey}
            level={1}
            open={isOpen(subKey)}
            label={group.subscriptionName}
            count={allSubResources.length}
            multiSelect={multiSelect}
            checked={triState(subEnabled, selectedIds)}
            tabStop={tabStopKey === subKey}
            inert={inert}
            onActivate={() => setActiveKey(subKey)}
            onToggleOpen={() => setOpen(subKey, !isOpen(subKey))}
            onToggleChecked={(checked) => props.onToggleMany(subEnabled, checked)}
          >
            {group.kinds.map((kind) => {
              const kKey = kindKey(group.subscriptionId, kind.kind);
              const kindEnabled = groupEnabled.get(kKey) ?? [];
              return (
                <GroupRow
                  key={kKey}
                  rowKey={kKey}
                  level={2}
                  open={isOpen(kKey)}
                  label={kind.label}
                  count={kind.resources.length}
                  multiSelect={multiSelect}
                  checked={triState(kindEnabled, selectedIds)}
                  tabStop={tabStopKey === kKey}
                  inert={inert}
                  onActivate={() => setActiveKey(kKey)}
                  onToggleOpen={() => setOpen(kKey, !isOpen(kKey))}
                  onToggleChecked={(checked) => props.onToggleMany(kindEnabled, checked)}
                >
                  {kind.resources.map((resource) => (
                    <ResourceRow
                      key={resource.id}
                      resource={resource}
                      multiSelect={multiSelect}
                      confirmOnRow={confirmOnRow}
                      checked={selectedIds.has(resource.id)}
                      focused={focusedId === resource.id}
                      disabled={disabledIds.has(resource.id)}
                      inert={inert}
                      tabStop={tabStopKey === resource.id}
                      onActivate={() => {
                        setActiveKey(resource.id);
                        if (!inert) props.onFocus(resource);
                      }}
                      onToggle={(checked) => props.onToggleResource(resource.id, checked)}
                      onConfirm={() => props.onConfirmResource(resource)}
                    />
                  ))}
                </GroupRow>
              );
            })}
          </GroupRow>
        );
      })}
    </div>
  );
}

function cssEscape(value: string): string {
  const escape = (globalThis.CSS as { escape?: (v: string) => string } | undefined)?.escape;
  return escape ? escape(value) : value.replace(/["\\]/g, '\\$&');
}
